import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  CancelMappingForTaxInvoiceRequest,
  InvoiceMappingRequest,
  PurchaseCashReceiptMappingRequest,
  SalesCashReceiptMappingRequest,
  CorporateCardApprovalMappingRequest,
  MappingTaxInvoiceAndSplitToTransactionRequest,
  TaxInvoiceIntoLabelItemRequest,
  cancelMappingForTaxInvoice,
  getInvoiceMappings,
  getPurchaseCashReceiptMappings,
  getSalesCashReceiptMappings,
  getCorporateCardApprovalMappings,
  getTransactionLabelMappings,
  mapTaxInvoiceIntoLabelItem,
  mappingTaxInvoiceAndSplitToTransaction,
  mappingCheckCardApprovalsToTransaction,
  mappingCashReceiptsToTransaction,
  MappingCheckCardApprovalsToTransactionRequest,
  MappingCashReceiptsToTransactionRequest,
  mappingTransactionRequest,
  mappingTransactionToTaxInvoice,
  updateExpectedPaymentDate,
} from '~apis/mapping';
import { queryClient } from '~libs/queryClient';

import { TransactionsQueryKey } from '../transactions/query';

export const InvoiceMappingQueryKey = {
  default: ({ companyId }: { companyId: string }) => ['invoiceMappings', companyId],
  query: ({
    companyId,
    requestParams,
  }: {
    companyId: string;
    requestParams?: InvoiceMappingRequest;
  }) =>
    requestParams ? ['invoiceMappings', companyId, requestParams] : ['invoiceMappings', companyId],
};

const TransactionLabelMappingsQueryKey = {
  default: ({ companyId }: { companyId: string }) => ['transactionLabelMappings', companyId],
  transaction: ({ companyId, transactionId }: { companyId: string; transactionId: number }) => [
    'transactionLabelMappings',
    companyId,
    transactionId,
  ],
};

export const PurchaseCashReceiptMappingQueryKey = {
  default: ({ companyId }: { companyId: string }) => ['purchaseCashReceiptMappings', companyId],
  query: ({
    companyId,
    requestParams,
  }: {
    companyId: string;
    requestParams?: PurchaseCashReceiptMappingRequest;
  }) =>
    requestParams
      ? ['purchaseCashReceiptMappings', companyId, requestParams]
      : ['purchaseCashReceiptMappings', companyId],
};

export const SalesCashReceiptMappingQueryKey = {
  default: ({ companyId }: { companyId: string }) => ['salesCashReceiptMappings', companyId],
  query: ({
    companyId,
    requestParams,
  }: {
    companyId: string;
    requestParams?: SalesCashReceiptMappingRequest;
  }) =>
    requestParams
      ? ['salesCashReceiptMappings', companyId, requestParams]
      : ['salesCashReceiptMappings', companyId],
};

export function useGetInvoiceMappings({
  companyId,
  requestParams,
}: {
  companyId: string;
  requestParams: InvoiceMappingRequest;
}) {
  return useQuery({
    queryKey: InvoiceMappingQueryKey.query({
      companyId,
      requestParams,
    }),
    queryFn: () => getInvoiceMappings({ companyId, requestParams }),
    refetchOnMount: true,
    enabled: !!companyId,
  });
}

export function useGetPurchaseCashreceiptMappings({
  companyId,
  requestParams,
}: {
  companyId: string;
  requestParams: PurchaseCashReceiptMappingRequest;
}) {
  return useQuery({
    queryKey: PurchaseCashReceiptMappingQueryKey.query({ companyId, requestParams }),
    queryFn: () => getPurchaseCashReceiptMappings({ companyId, requestParams }),
    refetchOnMount: true,
    enabled: !!companyId,
  });
}

export function useGetSalesCashReceiptMappings({
  companyId,
  requestParams,
}: {
  companyId: string;
  requestParams: SalesCashReceiptMappingRequest;
}) {
  return useQuery({
    queryKey: SalesCashReceiptMappingQueryKey.query({ companyId, requestParams }),
    queryFn: () => getSalesCashReceiptMappings({ companyId, requestParams }),
    refetchOnMount: true,
    enabled: !!companyId,
  });
}

export const CorporateCardApprovalMappingQueryKey = {
  default: ({ companyId }: { companyId: string }) => ['corporateCardApprovalMappings', companyId],
  query: ({
    companyId,
    requestParams,
  }: {
    companyId: string;
    requestParams?: CorporateCardApprovalMappingRequest;
  }) =>
    requestParams
      ? ['corporateCardApprovalMappings', companyId, requestParams]
      : ['corporateCardApprovalMappings', companyId],
};

export function useGetCorporateCardMappings({
  companyId,
  requestParams,
}: {
  companyId: string;
  requestParams: CorporateCardApprovalMappingRequest;
}) {
  return useQuery({
    queryKey: CorporateCardApprovalMappingQueryKey.query({ companyId, requestParams }),
    queryFn: () => getCorporateCardApprovalMappings({ companyId, requestParams }),
    refetchOnMount: true,
    enabled: !!companyId,
  });
}

export function useUpdateInvoiceExpectedPaymentDate() {
  return useMutation({
    mutationFn: updateExpectedPaymentDate,
  });
}

export function useGetTransactionLabelMappings({
  companyId,
  transactionId,
}: {
  companyId: string;
  transactionId: number;
}) {
  return useQuery({
    queryKey: TransactionLabelMappingsQueryKey.transaction({
      companyId: companyId,
      transactionId: transactionId,
    }),
    queryFn: () => getTransactionLabelMappings({ companyId, transactionId }),
    refetchOnMount: true,
    enabled: !!companyId && !!transactionId,
  });
}

export function useMappingTransactionToTaxInvoice() {
  return useMutation({
    mutationFn: (variables: mappingTransactionRequest) => mappingTransactionToTaxInvoice(variables),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(TransactionsQueryKey.default());
      queryClient.invalidateQueries(
        TransactionLabelMappingsQueryKey.default({ companyId: variables.companyId }),
      );
      queryClient.invalidateQueries(
        InvoiceMappingQueryKey.query({
          companyId: variables.companyId,
        }),
      );
    },
  });
}

export function useMappingTaxInvoiceAndSplitToTransaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: MappingTaxInvoiceAndSplitToTransactionRequest) =>
      mappingTaxInvoiceAndSplitToTransaction(variables),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(TransactionsQueryKey.list({ companyId: variables.companyId }));
    },
  });
}

export function useMappingCheckCardApprovalsToTransaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: MappingCheckCardApprovalsToTransactionRequest) =>
      mappingCheckCardApprovalsToTransaction(variables),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(TransactionsQueryKey.list({ companyId: variables.companyId }));
    },
  });
}

export function useMappingCashReceiptsToTransaction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: MappingCashReceiptsToTransactionRequest) =>
      mappingCashReceiptsToTransaction(variables),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(TransactionsQueryKey.list({ companyId: variables.companyId }));
    },
  });
}

export function useCancelMappingForTaxInvoice() {
  return useMutation({
    mutationFn: (variables: { companyId: string; request: CancelMappingForTaxInvoiceRequest }) =>
      cancelMappingForTaxInvoice(variables),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(
        InvoiceMappingQueryKey.query({
          companyId: variables.companyId,
        }),
      );
    },
  });
}

export function useMapTaxInvoiceIntoLabelItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: TaxInvoiceIntoLabelItemRequest) =>
      mapTaxInvoiceIntoLabelItem(variables),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(TransactionsQueryKey.list({ companyId: variables.companyId }));
    },
  });
}
